import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseInfiniteQueryOptions, useInfiniteQuery, useQueryClient } from 'react-query'
import { BrandPlanSchedule } from 'types'
import { Response } from 'request-dot-js/index'
import qs from 'qs'
import { getNextPageParam } from 'utils/getNextPageParam'
import { reportError } from 'utils/error'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

type Data = { schedules: BrandPlanSchedule[] }

interface Params {
  page: number
  items: number
  start_date?: string
  end_date?: string
  order_direction?: 'asc' | 'desc'
  order_by?: 'start_date'
}

export interface Props
  extends Partial<Pick<Params, 'items' | 'start_date' | 'end_date' | 'order_direction' | 'order_by'>> {
  brandPlanId: number
}

export const fetchBrandPlanSchedules = async ({
  workspaceId,
  brandPlanId,
  params,
}: {
  workspaceId: number
  brandPlanId: number
  params: Params
}) => {
  const stringifiedParams = qs.stringify(params)
  const res = await sendToApi<{ schedules: BrandPlanSchedule[] }>(
    `/w/${workspaceId}/brand_plans/${brandPlanId}/schedules?${stringifiedParams}`,
  )
  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }
  return res
}

export interface Options<TData> extends UseInfiniteQueryOptions<Response<Data>, unknown, TData> {}

export const useBrandPlanSchedules = <TData = Response<Data>,>(
  { brandPlanId, items = 5, start_date, end_date, order_direction = 'desc', order_by = 'start_date', ...rest }: Props,
  options?: Options<TData>,
) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useInfiniteQuery<Response<Data>, unknown, TData>(
    [
      'workspaces',
      workspaceId,
      'brandPlans',
      brandPlanId,
      'schedules',
      'list',
      { items, start_date, end_date, order_direction, order_by },
    ],
    async ({ pageParam = 1 }) => {
      const result = await fetchBrandPlanSchedules({
        workspaceId,
        brandPlanId,
        params: { ...rest, page: pageParam, items, start_date, end_date, order_direction },
      })
      result.data.schedules.forEach((schedule) => {
        queryClient.setQueryData(['schedules', 'detail', schedule.id], schedule)
      })
      return result
    },
    {
      refetchOnWindowFocus: false,
      getNextPageParam,
      onError: (err) => {
        reportError(err, {
          severity: 'error',
        })
      },
      ...options,
    },
  )
}
